import { useState, useEffect } from 'react';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import uuid from 'uuid/v4';

import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFollowingFeedWithSortByStorageFeedContentQuery$data,
	useFollowingFeedWithSortByStorageFeedContentQuery$variables,
} from './__generated__/useFollowingFeedWithSortByStorageFeedContentQuery.graphql.ts';
import type {
	usePopularFeedWithTimeGranularityStorageQuery$data,
	usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';
import type {
	FollowingFeedWithSortByStorageSnippetsQuery$data,
	FollowingFeedWithSortByStorageSnippetsQuery$variables,
} from './__generated__/FollowingFeedWithSortByStorageSnippetsQuery.graphql.ts';
import type {
	PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
	PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsQuery.graphql.ts';

export const useFeedSession = (
	feedContentQuery:
		| UseQueryInteropSupportedResult<
				useFollowingFeedWithSortByStorageFeedContentQuery$data,
				useFollowingFeedWithSortByStorageFeedContentQuery$variables
		  >
		| UseQueryInteropSupportedResult<
				usePopularFeedWithTimeGranularityStorageQuery$data,
				usePopularFeedWithTimeGranularityStorageQuery$variables
		  >
		| UseQueryInteropSupportedResult<
				FollowingFeedWithSortByStorageSnippetsQuery$data,
				FollowingFeedWithSortByStorageSnippetsQuery$variables
		  >
		| UseQueryInteropSupportedResult<
				PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
				PopularFeedWithTimeGranularityStorageSnippetsQuery$variables
		  >,
	sessionStorageKey: string,
): string => {
	// Fetch the initial feed id from session storage or generate a new one
	const feedSessionIdInitial: string = feedContentQuery.data
		? sessionStorage?.getItem(sessionStorageKey) || uuid()
		: uuid();

	const [feedSessionId, setFeedSessionId] = useState<string>(feedSessionIdInitial);

	// Update the session storage with the new feed id if it changes
	useEffect(() => {
		sessionStorage?.setItem(sessionStorageKey, feedSessionId);
	}, [sessionStorageKey, feedSessionId]);

	// Regenerate session id when refetching
	useEffect(() => {
		if (feedContentQuery.networkStatus === NetworkStatus.refetch) {
			setFeedSessionId(uuid());
		}
	}, [feedContentQuery.networkStatus]);

	return feedSessionId;
};
